import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser(
    $planning_preference: String
    $target_timeframe: Int
    $business_continuity_state_id: ID
    $succession_planning_state_id: ID
    $workweek_trajectory_now: Int
    $workweek_trajectory_year_3: Int
    $workweek_trajectory_year_5: Int
    $workweek_trajectory_year_10: Int
    $workweek_trajectory_year_15: Int
    $workweek_trajectory_year_20: Int
    $workweek_trajectory_year_25: Int
  ) {
    updateCurrentUser(
      planning_preference: $planning_preference
      target_timeframe: $target_timeframe
      business_continuity_state_id: $business_continuity_state_id
      succession_planning_state_id: $succession_planning_state_id
      workweek_trajectory_now: $workweek_trajectory_now
      workweek_trajectory_year_3: $workweek_trajectory_year_3
      workweek_trajectory_year_5: $workweek_trajectory_year_5
      workweek_trajectory_year_10: $workweek_trajectory_year_10
      workweek_trajectory_year_15: $workweek_trajectory_year_15
      workweek_trajectory_year_20: $workweek_trajectory_year_20
      workweek_trajectory_year_25: $workweek_trajectory_year_25
    )
  }
`;

const ContinuityPlanningEditModal = ({ user, onClose, onSave, onError }) => {
  const intl = useIntl();
  const [planningPreference, setPlanningPreference] = useState(user.planning_preference || '');
  const [targetTimeframe, setTargetTimeframe] = useState(user.target_timeframe || '');
  const [businessContinuityStateId, setBusinessContinuityStateId] = useState(user.business_continuity_state_id || '');
  const [successionPlanningStateId, setSuccessionPlanningStateId] = useState(user.succession_planning_state_id || '');
  const [workweekNow, setWorkweekNow] = useState(user.workweek_trajectory_now || '');
  const [workweekYear3, setWorkweekYear3] = useState(user.workweek_trajectory_year_3 || '');
  const [workweekYear5, setWorkweekYear5] = useState(user.workweek_trajectory_year_5 || '');
  const [workweekYear10, setWorkweekYear10] = useState(user.workweek_trajectory_year_10 || '');
  const [workweekYear15, setWorkweekYear15] = useState(user.workweek_trajectory_year_15 || '');
  const [workweekYear20, setWorkweekYear20] = useState(user.workweek_trajectory_year_20 || '');
  const [workweekYear25, setWorkweekYear25] = useState(user.workweek_trajectory_year_25 || '');

  const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
    refetchQueries: ['GetCurrentUserProfile'],
    awaitRefetchQueries: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await updateUser({
        variables: {
          planning_preference: planningPreference,
          target_timeframe: targetTimeframe ? parseInt(targetTimeframe) : null,
          business_continuity_state_id: businessContinuityStateId || null,
          succession_planning_state_id: successionPlanningStateId || null,
          workweek_trajectory_now: workweekNow ? parseInt(workweekNow) : null,
          workweek_trajectory_year_3: workweekYear3 ? parseInt(workweekYear3) : null,
          workweek_trajectory_year_5: workweekYear5 ? parseInt(workweekYear5) : null,
          workweek_trajectory_year_10: workweekYear10 ? parseInt(workweekYear10) : null,
          workweek_trajectory_year_15: workweekYear15 ? parseInt(workweekYear15) : null,
          workweek_trajectory_year_20: workweekYear20 ? parseInt(workweekYear20) : null,
          workweek_trajectory_year_25: workweekYear25 ? parseInt(workweekYear25) : null,
        },
      });

      onSave('Continuity planning updated successfully');
    } catch (error) {
      console.error('Error updating continuity planning:', error);
      onError(error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
      <div className="bg-succession-bg-secondary rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 bg-succession-bg-secondary border-b border-succession-border p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.title' })}</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-succession-bg-primary rounded-full transition-colors"
            >
              <svg className="w-6 h-6 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            <div className="p-6 space-y-6">
              {/* Business Continuity Readiness */}
              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-3">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.business_continuity_label' })}
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    type="button"
                    onClick={() => setBusinessContinuityStateId('1')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      businessContinuityStateId === '1'
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.none_option' })}</span>
                      {businessContinuityStateId === '1' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBusinessContinuityStateId('3')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      businessContinuityStateId === '3'
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.partner_without_financing' })}</span>
                      {businessContinuityStateId === '3' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBusinessContinuityStateId('2')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      businessContinuityStateId === '2'
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.partner_with_financing' })}</span>
                      {businessContinuityStateId === '2' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                </div>
                <p className="mt-2 text-xs text-succession-text-muted">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.business_continuity_help' })}
                </p>
              </div>

              {/* Succession Planning Readiness */}
              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-3">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.succession_planning_label' })}
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    type="button"
                    onClick={() => setSuccessionPlanningStateId('1')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      successionPlanningStateId === '1'
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.none_option' })}</span>
                      {successionPlanningStateId === '1' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSuccessionPlanningStateId('3')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      successionPlanningStateId === '3'
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.plan_in_development' })}</span>
                      {successionPlanningStateId === '3' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSuccessionPlanningStateId('2')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      successionPlanningStateId === '2'
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.plan_complete' })}</span>
                      {successionPlanningStateId === '2' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                </div>
                <p className="mt-2 text-xs text-succession-text-muted">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.succession_planning_help' })}
                </p>
              </div>

              {/* Catastrophic Preference */}
              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-3">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.catastrophic_preference_label' })}
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    type="button"
                    onClick={() => setPlanningPreference('find_partner')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      planningPreference === 'find_partner'
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.find_partner' })}</span>
                      {planningPreference === 'find_partner' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlanningPreference('become_partner')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      planningPreference === 'become_partner'
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.become_partner' })}</span>
                      {planningPreference === 'become_partner' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlanningPreference('no_plan_preference')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      planningPreference === 'no_plan_preference'
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.no_preference' })}</span>
                      {planningPreference === 'no_plan_preference' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlanningPreference('')}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      planningPreference === ''
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.not_specified' })}</span>
                      {planningPreference === '' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                </div>
                <p className="mt-2 text-xs text-succession-text-muted">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.catastrophic_preference_help' })}
                </p>
              </div>

              {/* Planning Time Horizon */}
              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.planning_time_horizon_label' })}
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={targetTimeframe}
                  onChange={(e) => setTargetTimeframe(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.planning_time_horizon_placeholder' })}
                  className="w-full px-4 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                />
                <p className="mt-1 text-xs text-succession-text-muted">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.planning_time_horizon_hint' })}
                </p>
              </div>

              {/* Workweek Trajectory */}
              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-3">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_trajectory_label' })}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_now' })}</label>
                    <input
                      type="number"
                      min="0"
                      max="168"
                      value={workweekNow}
                      onChange={(e) => setWorkweekNow(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_hours_placeholder' })}
                      className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_year_3' })}</label>
                    <input
                      type="number"
                      min="0"
                      max="168"
                      value={workweekYear3}
                      onChange={(e) => setWorkweekYear3(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_hours_placeholder' })}
                      className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_year_5' })}</label>
                    <input
                      type="number"
                      min="0"
                      max="168"
                      value={workweekYear5}
                      onChange={(e) => setWorkweekYear5(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_hours_placeholder' })}
                      className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_year_10' })}</label>
                    <input
                      type="number"
                      min="0"
                      max="168"
                      value={workweekYear10}
                      onChange={(e) => setWorkweekYear10(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_hours_placeholder' })}
                      className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_year_15' })}</label>
                    <input
                      type="number"
                      min="0"
                      max="168"
                      value={workweekYear15}
                      onChange={(e) => setWorkweekYear15(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_hours_placeholder' })}
                      className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_year_20' })}</label>
                    <input
                      type="number"
                      min="0"
                      max="168"
                      value={workweekYear20}
                      onChange={(e) => setWorkweekYear20(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_hours_placeholder' })}
                      className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_year_25' })}</label>
                    <input
                      type="number"
                      min="0"
                      max="168"
                      value={workweekYear25}
                      onChange={(e) => setWorkweekYear25(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_hours_placeholder' })}
                      className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                    />
                  </div>
                </div>
                <p className="mt-2 text-xs text-succession-text-muted">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.continuity_planning_edit.workweek_hint' })}
                </p>
              </div>
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="flex-shrink-0 flex justify-end gap-3 p-6 border-t border-succession-border bg-succession-bg-secondary">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors disabled:opacity-50"
            >
              {intl.formatMessage({ id: 'succession.profile_edit.common.cancel' })}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
            >
              {loading && (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? intl.formatMessage({ id: 'succession.profile_edit.common.saving' }) : intl.formatMessage({ id: 'succession.profile_edit.common.save_changes' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ContinuityPlanningEditModal.propTypes = {
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default ContinuityPlanningEditModal;
